import { Box, type SxProps, type Theme } from "@mui/material";
import type { ReactNode } from "react";

interface ContentCoverProps {
  children: ReactNode;
  /**
   * Shown in place of `children`, in the same space. While set, `children`
   * stay mounted (state, caret and form value survive) but are hidden.
   */
  cover?: ReactNode;
  sx?: SxProps<Theme>;
}

/** Both layers share the one grid cell. */
const LAYER_SX = {
  gridArea: "1 / 1",
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  minHeight: 0,
} as const;

/**
 * Stacks `cover` over `children` in one grid cell, so the cover always has
 * the content's width — it follows a responsive container with nothing
 * measured — and at least its height, growing only if the cover needs more.
 * Given a fixed height (`sx`), both layers keep to it: content that scrolls
 * inside itself still scrolls.
 * Both layers are column flex boxes: a child with `flex: 1` fills the cell.
 */
export const ContentCover = ({ children, cover, sx }: ContentCoverProps) => {
  const isCovered = Boolean(cover);

  return (
    <Box
      sx={[
        {
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gridTemplateRows: "minmax(0, 1fr)",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        sx={{
          ...LAYER_SX,
          visibility: isCovered ? "hidden" : "visible",
        }}
      >
        {children}
      </Box>
      {isCovered && <Box sx={LAYER_SX}>{cover}</Box>}
    </Box>
  );
};
